import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { database, txn } from '@/db';
import { Customer } from '@/db/models/Customers';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { logActivity } from '@/utils/auditLog';

interface Props {
  storeId: string;
  customerId?: string | null;
  onSaved: () => void;
  onCancel: () => void;
}

export function CustomerForm({ storeId, customerId, onSaved, onCancel }: Props) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [gstin, setGstin] = useState('');
  const [pan, setPan] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [creditLimit, setCreditLimit] = useState('0');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  useEffect(() => {
    if (!customerId) return;
    let alive = true;
    database.get<Customer>('customers').find(customerId).then((c) => {
      if (!alive) return;
      setName(c.name ?? '');
      setPhone(c.phone ?? '');
      setEmail(c.email ?? '');
      setGstin(c.gstin ?? '');
      setPan(c.pan ?? '');
      setAddress(c.address ?? '');
      setCity(c.city ?? '');
      setState(c.state ?? '');
      setPincode(c.pincode ?? '');
      setCreditLimit(String(c.creditLimit ?? 0));
      setOpeningBalance(String(c.balance ?? 0));
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [customerId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName && !trimmedPhone) {
      setError('Enter a customer name or phone number.');
      return;
    }
    if (trimmedPhone && !/^\d{10}$/.test(trimmedPhone)) {
      setError('Phone must be a 10-digit number.');
      return;
    }
    if (gstin.trim() && gstin.trim().length !== 15) {
      setError('GSTIN must be 15 characters.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const limit = Number(creditLimit) || 0;
      const balance = Number(openingBalance) || 0;
      let savedId = customerId ?? '';
      let editDiff: { before: Record<string, unknown>; after: Record<string, unknown> } | null = null;
      await txn(async (db) => {
        if (customerId) {
          const existing = await db.get<Customer>('customers').find(customerId);
          const before: Record<string, unknown> = {};
          const after: Record<string, unknown> = {};
          const track: [string, unknown, unknown][] = [
            ['name', existing.name, trimmedName || null],
            ['phone', existing.phone, trimmedPhone || null],
            ['creditLimit', existing.creditLimit, Math.max(0, limit)],
            ['balance', existing.balance, Math.max(0, balance)],
            ['gstin', existing.gstin, gstin.trim().toUpperCase() || null],
          ];
          for (const [k, oldV, newV] of track) {
            if (oldV !== newV) { before[k] = oldV; after[k] = newV; }
          }
          if (Object.keys(after).length > 0) editDiff = { before, after };
          await existing.update((record) => {
            record.name = trimmedName || null;
            record.phone = trimmedPhone || null;
            record.email = email.trim() || null;
            record.gstin = gstin.trim().toUpperCase() || null;
            record.pan = pan.trim().toUpperCase() || null;
            record.address = address.trim() || null;
            record.city = city.trim() || null;
            record.state = state.trim() || null;
            record.pincode = pincode.trim() || null;
            record.creditLimit = Math.max(0, limit);
            record.balance = Math.max(0, balance);
            record.version = existing.version + 1;
            record.isSynced = false;
          });
        } else {
          const created = await db.get<Customer>('customers').create((record) => {
            record.storeId = storeId;
            record.name = trimmedName || null;
            record.phone = trimmedPhone || null;
            record.email = email.trim() || null;
            record.gstin = gstin.trim().toUpperCase() || null;
            record.pan = pan.trim().toUpperCase() || null;
            record.address = address.trim() || null;
            record.city = city.trim() || null;
            record.state = state.trim() || null;
            record.pincode = pincode.trim() || null;
            record.groupId = null;
            record.creditLimit = Math.max(0, limit);
            record.balance = Math.max(0, balance);
            record.loyaltyTierId = null;
            record.loyaltyPoints = 0;
            record.totalSpend = 0;
            record.visitCount = 0;
            record.lastVisitAt = null;
            record.birthday = null;
            record.anniversary = null;
            record.tags = null;
            record.version = 1;
            record.isSynced = false;
            record.isDeleted = false;
            record.lastSyncBalance = null;
            record.lastSyncTotalSpend = null;
            record.lastSyncLoyaltyPoints = null;
            record.lastSyncVisitCount = null;
          });
          savedId = created.id;
        }
      });

      const label = trimmedName || trimmedPhone || 'Customer';
      if (customerId) {
        if (editDiff) {
          const d = editDiff as { before: Record<string, unknown>; after: Record<string, unknown> };
          void logActivity({
            action: 'customer_updated',
            entityType: 'customer',
            entityId: savedId,
            storeId,
            changes: { summary: `${label} · ${Object.keys(d.after).join(', ')} changed`, before: d.before, after: d.after },
          });
        }
      } else {
        void logActivity({
          action: 'customer_created',
          entityType: 'customer',
          entityId: savedId,
          storeId,
          changes: { summary: `${label}${trimmedPhone ? ` · ${trimmedPhone}` : ''}`, amount: Math.max(0, balance) || undefined },
        });
      }
      notifySuccess(customerId ? `Customer “${label}” updated.` : `Customer “${label}” added.`);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save customer.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-5">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
        <Field label="Phone"><Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} /></Field>
        <Field label="Email"><Input value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="GSTIN"><Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase().slice(0, 15))} /></Field>
        <Field label="PAN"><Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))} /></Field>
        <Field label="Credit limit"><Input type="number" min="0" step="0.01" value={creditLimit} onChange={(e) => setCreditLimit(e.target.value)} /></Field>
        <Field label="Opening/current balance"><Input type="number" min="0" step="0.01" value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} /></Field>
        <Field label="Pincode"><Input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))} /></Field>
      </div>
      <Field label="Address"><Input value={address} onChange={(e) => setAddress(e.target.value)} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="City"><Input value={city} onChange={(e) => setCity(e.target.value)} /></Field>
        <Field label="State"><Input value={state} onChange={(e) => setState(e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save customer'}</Button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-label uppercase text-fg-tertiary">{label}</span>
      {children}
    </label>
  );
}
